import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { StatCard } from "@/components/kit";
import { Inbox, CheckCircle2, XCircle, Copy, FileText, Wine, Newspaper, BarChart3, Check, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const TYPE_ICON = { Recipe: FileText, Product: Wine, Article: Newspaper, Ranking: BarChart3 };
const TABS = ["all", "Recipe", "Product", "Article", "Ranking"];

export default function Admin() {
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("all");

  const load = () => { api.get("/admin/queue").then((r) => setQueue(r.data)); api.get("/admin/stats").then((r) => setStats(r.data)); };
  useEffect(() => { load(); }, []);

  const moderate = async (qid, action) => {
    await api.post(`/admin/queue/${qid}`, { action });
    toast.success(action === "approve" ? "Published to platform" : "Rejected");
    load();
  };

  const rows = queue.filter((q) => tab === "all" ? true : q.type === tab);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 lg:px-6">
      <div className="mb-6 flex items-center gap-2"><ShieldCheck className="text-primary" /><div><p className="overline text-primary">Content operations</p><h1 className="font-display text-4xl">Ingestion dashboard</h1></div></div>

      {stats && (
        <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard icon={Inbox} label="Pending review" value={stats.pending} />
          <StatCard icon={CheckCircle2} label="Approved" value={stats.approved} />
          <StatCard icon={XCircle} label="Rejected" value={stats.rejected} />
          <StatCard icon={Copy} label="Duplicates flagged" value={stats.duplicates} />
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map((t) => <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)} className={`rounded-full border px-4 py-1.5 text-sm font-medium ${tab === t ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>{t === "all" ? "All queues" : t}</button>)}
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="p-4">Item</th><th className="hidden p-4 sm:table-cell">Category</th><th className="hidden p-4 md:table-cell">Source</th><th className="p-4">Confidence</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((q) => { const Icon = TYPE_ICON[q.type] || FileText; return (
              <tr key={q.id} data-testid={`queue-row-${q.id}`} className="hover:bg-muted/30">
                <td className="p-4"><div className="flex items-center gap-2"><Icon size={16} className="text-primary" /><div><p className="font-medium">{q.title}</p><span className="text-xs text-muted-foreground">{q.type}</span>{q.dupe && <span className="ml-2 rounded-full bg-destructive/10 px-2 py-0.5 text-xs text-destructive">Possible duplicate</span>}</div></div></td>
                <td className="hidden p-4 text-muted-foreground sm:table-cell">{q.category}</td>
                <td className="hidden p-4 text-muted-foreground md:table-cell">{q.source}</td>
                <td className="p-4"><div className="flex items-center gap-2"><div className="h-1.5 w-16 rounded-full bg-muted"><div className={`h-full rounded-full ${q.confidence > 0.85 ? "bg-secondary" : "bg-primary"}`} style={{ width: `${q.confidence * 100}%` }} /></div><span className="text-xs">{Math.round(q.confidence * 100)}%</span></div></td>
                <td className="p-4"><StatusPill status={q.status} /></td>
                <td className="p-4">
                  {q.status === "pending" ? (
                    <div className="flex justify-end gap-2">
                      <button data-testid={`approve-${q.id}`} onClick={() => moderate(q.id, "approve")} className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold text-secondary-foreground"><Check size={13} /> Publish</button>
                      <button data-testid={`reject-${q.id}`} onClick={() => moderate(q.id, "reject")} className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:border-destructive hover:text-destructive"><X size={13} /> Reject</button>
                    </div>
                  ) : <span className="block text-right text-xs text-muted-foreground">Actioned</span>}
                </td>
              </tr>
            ); })}
          </tbody>
        </table>
      </div>
      <p className="mt-4 text-xs text-muted-foreground">Duplicate detection, category mapping, brand normalization, affiliate link mapping and compliance review run before publish. Nothing goes live without approval.</p>
    </div>
  );
}

function StatusPill({ status }) {
  const map = { pending: "bg-primary/10 text-primary", approved: "bg-secondary/15 text-secondary dark:text-secondary-foreground", rejected: "bg-destructive/10 text-destructive" };
  return <span className={`rounded-full px-2.5 py-1 text-xs font-medium capitalize ${map[status]}`}>{status}</span>;
}
